import React, { useState, useEffect, useRef } from 'react';
import { devLog, devError } from '../utils/devLogger';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay, Navigation, Zoom } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import ImageView from '../components/ImageView';
import FavoriteIcon from './common/FavoriteIcon';
import { useUserAuth } from '../context/UserAuthContext';
import { likeLot } from '../lib/api/user/userApi';

// Swiper 스타일 import

// iOS 최적화된 이미지 컴포넌트 (로딩 속도 개선 + 검정색 이미지 문제 해결)
const OptimizedCarouselImage = ({ src, alt, isModal = false, ...props }: { src: string; alt: string; isModal?: boolean; [key: string]: any }) => {
  const [currentSrc, setCurrentSrc] = useState(src);
  const [hasError, setHasError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  
  const handleLoad = () => {
    // iOS Safari에서 이미지 로드 완료 후 약간의 지연을 주어 렌더링 안정화
    setTimeout(() => {
      setIsLoaded(true);
    }, 15); // 모달용으로 약간 더 긴 지연
  };
  
  const handleError = () => {
    if (!hasError && !src.startsWith("data:image/svg+xml")) {
      setHasError(true);
      setCurrentSrc("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTIyIDEyQzIyIDEzLjk3NzggMjEuNDE0IDE1Ljg2NjYgMjAuMzcxNCAxNy40ODI5QzE5LjMyODkgMTkuMDk5MiAxNy44NzQ0IDIwLjM3MjYgMTYuMjAyOCAyMS4xNzU2QzE0LjUzMTMgMjEuOTc4NyAxMi43MTA2IDIyLjI5NTQgMTAuOTAzNyAyMi4wOTc2QzkuMDk2NzIgMjEuODk5OCA3LjM4NDMzIDIxLjE5NTMgNiAyMC4wOTEyQzQuNjE1NjcgMTguOTg3MSAzLjUzNzI1IDE3LjUxNjkgMi44NjMwNyAxNS44NDM3QzIuMTg4OSAxNC4xNzA0IDEuOTQ2NDMgMTIuMzQ2NSAyLjE3OTY3IDEwLjU1MDNDMi40MTI5MSA4Ljc1NDEzIDMuMTEzODUgNy4wNDcxOSA0LjIxNDcgNS42MTE4QzUuMzE1NTUgNC4xNzY0IDYuNzcyNjEgMy4wNjQ4MiA4LjQzNDMgMi4zNzQ3NyIgc3Ryb2tlPSIjY2NjIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIvPgo8Y2lyY2xlIGN4PSIxMiIgY3k9IjEyIiByPSIzIiBzdHJva2U9IiNjY2MiIHN0cm9rZS13aWR0aD0iMiIvPgo8L3N2Zz4K");
      setIsLoaded(true);
    }
  };
  
  // src가 변경되면 상태 리셋
  useEffect(() => {
    if (src !== currentSrc && !hasError) {
      setCurrentSrc(src);
      setIsLoaded(false);
    }
    if (src !== currentSrc) {
      setHasError(false);
      setIsLoaded(false);
    }
  }, [src, currentSrc, hasError]);
  
  return (
    <>
      {/* 로딩 플레이스홀더 - 모달에서는 투명하게 처리 */}
      {!isLoaded && !isModal && (
        <div style={{
          width: '100%',
          height: '100%',
          backgroundColor: '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'absolute',
          top: 0,
          left: 0,
          // iOS Safari 최적화
          WebkitTransform: 'translateZ(0)',
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
        }}>
          {/* 빈 placeholder */}
        </div>
      )}
      
      <img
        src={currentSrc}
        alt={alt}
        onLoad={handleLoad}
        onError={handleError}
        loading="eager"
        decoding="sync"
        style={{
          objectFit: 'contain',
          objectPosition: 'center',
          width: '100%',
          height: '100%',
          display: isLoaded ? 'block' : 'none', // 로드 완료 전까지 숨김
          opacity: isLoaded ? 1 : 0,
          transition: 'opacity 0.2s ease',
          // iOS Safari 최적화
          WebkitTransform: 'translateZ(0)',
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          ...props.style
        }}
        {...props}
      />
    </>
  );
};

// Swiper 모달용 스타일드 컴포넌트들
const FullScreenModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.95);
  z-index: 9999999; /* 매우 높은 z-index로 설정 (모바일 앱바 위에 표시) */
  display: flex;
  align-items: center;
  justify-content: center;
  
  /* 모바일에서 확실한 최상위 표시를 위한 추가 속성 */
  isolation: isolate;
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  will-change: transform;
  
  /* iOS에서 터치 이벤트 충돌 방지 */
  touch-action: none;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
`;

const SwiperContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  
  /* iOS Safari에서 Swiper 터치 이벤트 보장 */
  touch-action: pan-x pan-y pinch-zoom;
  -webkit-touch-callout: none;
  
  .swiper {
    width: 100%;
    height: 100%;
    
    /* iOS에서 Swiper 터치 이벤트 최적화 */
    touch-action: pan-x pan-y pinch-zoom;
    
    /* Swiper 내부 클릭은 모달 닫기 방지 */
    & * {
      pointer-events: auto;
    }
  }
  
  .swiper-slide {
    display: flex;
    align-items: center;
    justify-content: center;
    
    /* iOS에서 슬라이드 터치 최적화 */
    touch-action: pan-x pan-y pinch-zoom;
  }
  
  .swiper-zoom-container {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    
    /* 줌 컨테이너에서 터치 이벤트 허용 */
    touch-action: pan-x pan-y pinch-zoom;
  }
  
  .swiper-button-next,
  .swiper-button-prev {
    color: white;
    z-index: 999998; /* FullScreenModal보다 낮게 설정 */
    
    /* 모바일에서는 네비게이션 버튼 숨기기 */
    @media (max-width: 768px) {
      display: none;
    }
  }
  
  .swiper-pagination {
    z-index: 999998; /* FullScreenModal보다 낮게 설정 */
  }
  
  .swiper-pagination-bullet {
    background: white;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.8);
  border: 2px solid rgba(255, 255, 255, 0.3);
  font-size: 32px;
  font-weight: bold;
  color: #fff;
  cursor: pointer;
  z-index: 99999999; /* FullScreenModal보다 높게 설정 */
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  
  /* iOS에서 터치 이벤트 최적화 */
  touch-action: manipulation;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
  
  /* 호버 효과 */
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.5);
  }
  
  /* 활성화 효과 */
  &:active {
    transform: scale(0.95);
    background: rgba(255, 255, 255, 0.1);
  }
  
  /* 모바일에서 더 큰 터치 영역 */
  @media (max-width: 768px) {
    width: 56px;
    height: 56px;
    font-size: 36px;
    top: 16px;
    right: 16px;
  }
`;

// 모달용 이미지 컴포넌트 (Swiper 내부에서 사용)
const ModalImage = styled(OptimizedCarouselImage)`
  max-width: none; /* 줌 허용을 위해 max-width 제한 해제 */
  max-height: none; /* 줌 허용을 위해 max-height 제한 해제 */
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  touch-action: pan-x pan-y pinch-zoom; /* iOS에서 Swiper와 줌 모두 허용 */
  user-select: none;
  
  /* 줌 시 중심점 고정 */
  transform-origin: center center;
  
  /* iOS Safari 터치 이벤트 최적화 */
  -webkit-touch-callout: none;
  -webkit-user-select: none;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  /* margin-bottom: 24px; */
`;

const CarouselGrid = styled.div`
  .swiper {
    padding: 0 0 40px 0;
    overflow: visible;
  }
  
  .swiper-wrapper {
    overflow: visible;
  }
  
  .swiper-slide {
    height: auto;
    overflow: visible;
  }
  
  .swiper-pagination-bullet {
    background: #ddd;
    opacity: 1;
  }
  
  .swiper-pagination-bullet-active {
    background: #007bff;
  }
`;

const SlideCard = styled.div`
  background: white;
  border-radius: 8px;
  overflow: visible;
  width: 100%;
  max-width: 100%;
`;

const CardImageContainer = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1; /* 정사각형 비율 유지 */
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  padding: 0; /* 내부 여백 제거 - 이미지가 영역을 꽉 채우도록 */
  box-sizing: border-box;
  cursor: pointer; /* 클릭 가능함을 표시 */
  
  /* iOS Safari 터치 최적화 */
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
`;

// 사전 입찰자 배지 (이미지 하단 오버레이)
const PreBidderBadge = styled.div<{ variant?: 'top' | 'second' }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 14px 16px; /* 높이 살짝 증가 */
  background-color: ${({ variant }) => (variant === 'second' ? '#f44336' : '#a6d4d1')};
  color: ${({ variant }) => (variant === 'second' ? '#ffffff' : '#262626')};
  font-size: 15px;
  font-weight: 700; /* 더 굵게 */
  text-align: center;
  box-sizing: border-box;
  opacity: 0.9; /* 투명도 감소로 색을 더 진하게 */
  border-radius: 0 0 8px 8px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
`;


interface ImageCarouselProps {
  images?: string[];
  lotId?: number; // 실제 LOT ID
  likedUserIndexes?: number[]; // 좋아요한 사용자들의 index 배열
  onFavoriteToggle?: (index: number) => void;
  onFavoriteUpdate?: (newLikedUserIndexes: number[]) => void; // 좋아요 상태 업데이트 콜백
  expandable?: boolean; // 클릭 시 확장 가능 여부
  onLoginRequired?: () => void; // 로그인이 필요할 때 호출할 콜백
  onAdditionalInfoRequired?: () => void; // 추가정보 입력이 필요할 때 호출할 콜백
  // 사전 입찰자 배지 표시용
  isTopPreBidder?: boolean;
  isSecondPreBidder?: boolean;
  topPreBidderLabel?: string;
  secondPreBidderLabel?: string;
}

// 더미 이미지 데이터
const dummyImages = [
  {
    id: 1,
    imageUrl: 'https://example.com/image1.jpg',
    isFavorite: true
  },
  {
    id: 2,
    imageUrl: 'https://example.com/image2.jpg',
    isFavorite: false
  },
  {
    id: 3,
    imageUrl: '',
    isFavorite: true
  },
  {
    id: 4,
    imageUrl: 'https://invalid-url.com/image.jpg',
    isFavorite: false
  }
];

const ImageCarousel: React.FC<ImageCarouselProps> = ({ 
  images = [], 
  lotId,
  likedUserIndexes,
  onFavoriteToggle,
  onFavoriteUpdate,
  expandable = true,
  onLoginRequired,
  onAdditionalInfoRequired,
  isTopPreBidder = false,
  isSecondPreBidder = false,
  topPreBidderLabel,
  secondPreBidderLabel
}) => {
  const { user } = useUserAuth();
  
  // 전달받은 images가 없으면 더미 데이터 사용
  const [imageData, setImageData] = useState(
    images.length > 0
      ? images.map((url, index) => ({ id: index + 1, imageUrl: url, isFavorite: false }))
      : dummyImages
  );
  const [optimisticFavorite, setOptimisticFavorite] = useState<{
    lotId: number;
    isFavorite: boolean;
  } | null>(null);

  const getLotId = (index: number) => lotId ?? imageData[index].id;

  const isLikedByUser = (index: number) => {
    const targetLotId = getLotId(index);
    if (optimisticFavorite?.lotId === targetLotId) {
      return optimisticFavorite.isFavorite;
    }
    if (user?.index === undefined || !likedUserIndexes || !Array.isArray(likedUserIndexes)) {
      return false;
    }
    return likedUserIndexes.includes(user.index);
  };

  // 모달 상태
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalSelectedIndex, setModalSelectedIndex] = useState(0);

  // 터치 이벤트 상태 (이미지 클릭 감지용)
  const startX = useRef<number | null>(null);
  const startY = useRef<number | null>(null);
  const isDragging = useRef(false);
  const hasMoved = useRef(false);
  const clickStartTime = useRef<number>(0);

  // images prop이 변경될 때마다 imageData 업데이트
  useEffect(() => {
    if (images.length > 0) {
      setImageData(images.map((url, index) => ({ id: index + 1, imageUrl: url, isFavorite: false })));
    }
  }, [images]);

  useEffect(() => {
    setOptimisticFavorite(null);
  }, [lotId]);

  // 터치 시작 (이미지 클릭 감지용)
  const handleImageTouchStart = (e: React.TouchEvent, index: number) => {
    const touches = e.touches;
    if (touches.length === 1) {
      const touch = touches[0];
      startX.current = touch.clientX;
      startY.current = touch.clientY;
      isDragging.current = true;
      hasMoved.current = false;
      clickStartTime.current = Date.now();
    }
  };

  // 터치 이동 (이미지 클릭 감지용)
  const handleImageTouchMove = (e: React.TouchEvent) => {
    const touches = e.touches;
    
    if (touches.length === 1 && isDragging.current && startX.current !== null && startY.current !== null) {
      const touch = touches[0];
      const deltaX = Math.abs(touch.clientX - startX.current);
      const deltaY = Math.abs(touch.clientY - startY.current);
      
      // 약간의 움직임도 감지하여 드래그로 간주
      if (deltaX > 5 || deltaY > 5) {
        hasMoved.current = true;
      }
    }
  };

  // 터치 종료 (이미지 클릭 감지용)
  const handleImageTouchEnd = (e: React.TouchEvent, index: number) => {
    const touchDuration = Date.now() - clickStartTime.current;
    const isTap = touchDuration < 300 && !hasMoved.current;
    
    if (isTap && expandable) {
      e.preventDefault();
      e.stopPropagation();
      setModalSelectedIndex(index);
      setShowImageModal(true);
    }
    
    isDragging.current = false;
    hasMoved.current = false;
    startX.current = null;
    startY.current = null;
  };

  // 마우스 이벤트 (데스크톱)
  const handleImageClick = (index: number) => {
    if (expandable) {
      setModalSelectedIndex(index);
      setShowImageModal(true);
    }
  };

  // 모달 닫기
  const closeModal = () => {
    setShowImageModal(false);
  };

  // ESC 키로 모달 닫기
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showImageModal) {
        closeModal();
      }
    };

    if (showImageModal) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showImageModal]);

  // 모달 배경 클릭 시에만 닫기 (Swiper 영역 제외)
  const handleModalBackgroundClick = (e: React.MouseEvent) => {
    e.preventDefault();
    
    const target = e.target as HTMLElement;
    const swiperContainer = target.closest('.swiper-container-class');
    const isSwiper = target.closest('.swiper');
    const isCloseButton = target.closest('.close-button');
    
    if (!swiperContainer && !isSwiper && !isCloseButton) {
      closeModal();
    }
  };

  const handleFavoriteClick = async (index: number, e: React.SyntheticEvent) => {
    e.stopPropagation();
    
    // 로그인 상태 확인
    if (!user) {
      devLog('사용자가 로그인하지 않았습니다.');
      if (onLoginRequired) {
        onLoginRequired();
      }
      return;
    }
    
    // 추가정보 입력 필요 여부 확인 (이름 / 연락처 / 국가 중 하나라도 비어 있으면)
    const isNameEmpty = !user.name || user.name.trim() === '';
    const isLocationEmpty = !user.locationType || user.locationType.trim() === '';
    // 연락처는 UserAuthContext에 없으므로 서버 기준으로 needMoreInfo만 사용
    if (isNameEmpty || isLocationEmpty || user.needMoreInfo) {
      devLog('추가정보 입력이 필요합니다. (이름/국가/needMoreInfo 체크)');
      if (onAdditionalInfoRequired) {
        onAdditionalInfoRequired();
      }
      return;
    }
    
    const targetLotId = getLotId(index);
    const isFavorite = isLikedByUser(index);
    const newLikedUserIndexes = isFavorite
      ? (likedUserIndexes ?? []).filter(userIndex => userIndex !== user.index)
      : [...(likedUserIndexes ?? []), user.index];

    setOptimisticFavorite({ lotId: targetLotId, isFavorite: !isFavorite });
    onFavoriteUpdate?.(newLikedUserIndexes);

    void likeLot(targetLotId).catch((err) => {
      devError('좋아요 처리 실패:', err);
    });

    if (onFavoriteToggle) {
      onFavoriteToggle(index);
    }
  };

  return (
    <Container>
      <CarouselGrid>
        <Swiper
          modules={imageData.length > 1 ? [Pagination, Autoplay] : [Pagination]}
          spaceBetween={20}
          slidesPerView={1}
          pagination={{ clickable: true }}
          autoplay={imageData.length > 1 ? {
            delay: 3000,
            disableOnInteraction: false,
          } : false}
          watchOverflow={true}
          style={{ overflow: 'visible' }}
        >
          {imageData.map((item, index) => (
            <SwiperSlide key={item.id}>
              <SlideCard>
                <CardImageContainer
                  onTouchStart={(e) => handleImageTouchStart(e, index)}
                  onTouchMove={handleImageTouchMove}
                  onTouchEnd={(e) => handleImageTouchEnd(e, index)}
                  onClick={() => handleImageClick(index)}
                >
                  <ImageView
                    imageUrl={item.imageUrl}
                    width="100%"
                    height="100%"
                    borderRadius={8}
                    alt={`이미지 ${index + 1}`}
                    objectFit="cover"
                  />
                  
                  {/* Favorite Icon - 사용자 좋아요 상태에 따른 FavoriteIcon */}
                  <FavoriteIcon
                    isFavorite={isLikedByUser(index)}
                    onClick={(e) => handleFavoriteClick(index, e)}
                    onTouchStart={(e) => e.stopPropagation()}
                    onTouchEnd={(e) => e.stopPropagation()}
                  />

                  {/* 최고/차순위 사전 입찰자 배지 - 이미지 하단 오버레이 */}
                  {(isTopPreBidder || isSecondPreBidder) && (
                    <PreBidderBadge variant={isTopPreBidder ? 'top' : 'second'}>
                      {isTopPreBidder
                        ? topPreBidderLabel || '최고 사전 입찰자'
                        : secondPreBidderLabel || '경쟁 최고가 발생'}
                    </PreBidderBadge>
                  )}
                </CardImageContainer>
              </SlideCard>
            </SwiperSlide>
          ))}
        </Swiper>
      </CarouselGrid>

      {/* Swiper를 사용한 이미지 모달 */}
      {showImageModal && expandable && (
        <FullScreenModal onClick={handleModalBackgroundClick}>
          <SwiperContainer className="swiper-container-class">
            <Swiper
              modules={[Navigation, Pagination, Zoom]}
              navigation={true}
              pagination={{ clickable: true }}
              zoom={true}
              initialSlide={modalSelectedIndex}
              onSlideChange={(swiper: SwiperType) => setModalSelectedIndex(swiper.activeIndex)}
              style={{ width: '100%', height: '100%' }}
            >
              {imageData.map((item, i) => (
                <SwiperSlide key={i}>
                  <div className="swiper-zoom-container">
                    <ModalImage
                      src={item.imageUrl}
                      alt={`modal-image-${i + 1}`}
                      isModal={true}
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
            <CloseButton 
              className="close-button"
              onClick={closeModal}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                closeModal();
              }}
            >
              ×
            </CloseButton>
          </SwiperContainer>
        </FullScreenModal>
      )}
    </Container>
  );
};

export default ImageCarousel;
